/**
 * The forced password change.
 *
 * A user whose account is flagged ``must_change_pw`` — a freshly seeded
 * account, or one an administrator has reset — cannot reach any other screen
 * until they have chosen their own password. The flag exists precisely so a
 * published or admin-set credential is never a working long-term password;
 * before this gate the flag was set on the server and ignored by the client,
 * so the seeded super admin walked straight in on the demo password.
 */

import { LogOut } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { Button } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import { AuthFrame } from '@/features/auth/AuthFrame'
import { ChangePasswordForm, MIN_PASSWORD_LENGTH } from '@/features/auth/ChangePasswordForm'

export function ChangePasswordPage() {
  const { session, refresh, signOut } = useAuth()
  const toast = useToast()

  return (
    <AuthFrame
      title="Choose a password"
      titleAs="h1"
      subtitle={`Signed in as ${session?.user.display_name ?? ''}`}
      action={
        <Button
          variant="ghost"
          size="sm"
          icon={<LogOut className="h-4 w-4 flip-rtl" />}
          onClick={() => void signOut()}
        >
          Sign out
        </Button>
      }
    >
      <p className="mb-4 text-[13px] text-[var(--text-muted)]">
        Before you continue, please replace the password you signed in with. Choose at least{' '}
        {MIN_PASSWORD_LENGTH} characters, not your username, and one you have not used here before.
      </p>
      <ChangePasswordForm
        autoFocus
        onChanged={async () => {
          toast.success('Password changed', 'Signing you into your account.')
          // The flag is cleared server-side; reloading the session drops this gate.
          await refresh()
        }}
      />
    </AuthFrame>
  )
}
